import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { offlineStorage } from '../lib/offlineStorage';
import { usePWA } from '../components/PWAInstaller';
import { canonAPI } from '../lib/api';
import {
  Download,
  HardDrive,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileJson,
  Clock,
  Database,
  Wifi,
  WifiOff,
  Smartphone,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';

// Removed BACKEND_URL in favor of canonAPI

export default function OfflinePage() {
  const [hasOfflineData, setHasOfflineData] = useState(false);
  const [savedVersion, setSavedVersion] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [storageSize, setStorageSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { isInstalled, cacheCanonData, updateAvailable, applyUpdate } = usePWA();
  const { t } = useTranslation();

  useEffect(() => {
    checkOfflineStatus();

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkOfflineStatus = () => {
    setHasOfflineData(offlineStorage.hasOfflineData());
    setSavedVersion(offlineStorage.getVersion());
    setSavedAt(offlineStorage.getSavedAt());
    setStorageSize(offlineStorage.getStorageSize());
  };

  const handleSaveToDevice = async () => {
    setLoading(true);
    setProgress(10);

    try {
      setProgress(30);
      const response = await canonAPI.export();
      setProgress(70);

      const success = offlineStorage.saveCanon(response.data);
      setProgress(100);

      if (success) {
        toast.success('Canon saved to device successfully!');
        checkOfflineStatus();
      } else {
        toast.error('Failed to save to device storage');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to download canon data');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 500);
    }
  };

  const handleDownloadFile = async () => {
    setLoading(true);

    try {
      const response = await canonAPI.export();
      offlineStorage.exportAsFile(response.data);
      toast.success('Canon file downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download canon file');
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = () => {
    offlineStorage.clearCanon();
    toast.success('Offline data cleared');
    checkOfflineStatus();
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Unknown';
    return new Date(isoString).toLocaleString();
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="offline-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <HardDrive className="h-4 w-4" />
          <span>Offline Access</span>
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {t('offline.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('offline.subtitle')}
        </p>
      </div>

      {/* Connection Status */}
      <Alert className={`mb-6 ${isOnline ? 'border-green-500/50 bg-green-500/5' : 'border-amber-500/50 bg-amber-500/5'}`}>
        <div className="flex items-center space-x-2">
          {isOnline ? (
            <>
              <Wifi className="h-4 w-4 text-green-500" />
              <AlertDescription>{t('offline.status.online')}</AlertDescription>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4 text-amber-500" />
              <AlertDescription>{t('offline.status.offline')}</AlertDescription>
            </>
          )}
        </div>
      </Alert>

      {/* PWA Install Card */}
      {!isInstalled && (
        <Card className="mb-6 border-accent/30 bg-accent/5" data-testid="pwa-install-card">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-full bg-accent/10">
                <Smartphone className="h-6 w-6 text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">{t('offline.pwa.title')}</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('offline.pwa.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{t('offline.pwa.badges.worksOffline')}</Badge>
                  <Badge variant="outline">{t('offline.pwa.badges.fastLaunch')}</Badge>
                  <Badge variant="outline">{t('offline.pwa.badges.noAppStore')}</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* App Installed Status */}
      {isInstalled && (
        <Alert className="mb-6 border-green-500/50 bg-green-500/5">
          <div className="flex items-center space-x-2">
            <Smartphone className="h-4 w-4 text-green-500" />
            <AlertDescription>{t('offline.pwa.installed')}</AlertDescription>
          </div>
        </Alert>
      )}

      {/* Update Available */}
      {updateAvailable && (
        <Alert className="mb-6 border-blue-500/50 bg-blue-500/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <RefreshCw className="h-4 w-4 text-blue-500" />
              <AlertDescription>{t('offline.pwa.updateAvailable')}</AlertDescription>
            </div>
            <Button size="sm" onClick={applyUpdate}>
              {t('offline.pwa.updateNow')}
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Save to Device */}
        <Card data-testid="save-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Download className="h-5 w-5 mr-2" />
              {t('offline.save.title')}
            </CardTitle>
            <CardDescription>
              {t('offline.save.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {progress > 0 && (
              <Progress value={progress} className="mb-4" />
            )}

            <Button
              onClick={handleSaveToDevice}
              disabled={loading || !isOnline}
              className="w-full"
              data-testid="save-to-device-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <HardDrive className="h-4 w-4 mr-2" />
                  {hasOfflineData ? t('offline.save.buttonUpdate') : t('offline.save.button')}
                </>
              )}
            </Button>

            <Separator />

            <Button
              variant="outline"
              onClick={handleDownloadFile}
              disabled={loading || !isOnline}
              className="w-full"
              data-testid="download-file-btn"
            >
              <FileJson className="h-4 w-4 mr-2" />
              {t('offline.save.downloadJson')}
            </Button>

            {isInstalled && (
              <>
                <Separator />
                <Button
                  variant="outline"
                  onClick={() => {
                    cacheCanonData();
                    toast.success('Caching canon data for offline use...');
                  }}
                  disabled={!isOnline}
                  className="w-full"
                  data-testid="cache-pwa-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('offline.save.refreshCache')}
                </Button>
              </>
            )}

            <p className="text-xs text-muted-foreground">
              {t('offline.save.note')}
            </p>
          </CardContent>
        </Card>

        {/* Current Status */}
        <Card data-testid="status-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Database className="h-5 w-5 mr-2" />
              {t('offline.dataStatus.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasOfflineData ? (
              <>
                <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">{t('offline.dataStatus.saved')}</span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="text-muted-foreground mb-1 flex items-center">
                      <Badge variant="outline" className="mr-1">v</Badge>
                      {t('offline.dataStatus.version')}
                    </div>
                    <div className="font-medium">{savedVersion || 'Unknown'}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="text-muted-foreground mb-1 flex items-center">
                      <HardDrive className="h-3 w-3 mr-1" />
                      {t('offline.dataStatus.size')}
                    </div>
                    <div className="font-medium">{storageSize} MB</div>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-muted/50 text-sm">
                  <div className="text-muted-foreground mb-1 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    {t('offline.dataStatus.lastSaved')}
                  </div>
                  <div className="font-medium">{formatDate(savedAt)}</div>
                </div>

                <Separator />

                <Button
                  variant="outline"
                  onClick={handleClearData}
                  className="w-full text-destructive hover:text-destructive"
                  data-testid="clear-data-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('offline.dataStatus.clearData')}
                </Button>
              </>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">{t('offline.dataStatus.noData')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('offline.dataStatus.noDataDesc')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* What's Included */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-lg">{t('offline.included.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-4 rounded-lg bg-muted/50 text-center">
              <div className="text-2xl font-semibold text-accent">36</div>
              <div className="text-muted-foreground">{t('offline.included.articles')}</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 text-center">
              <div className="text-2xl font-semibold text-accent">11</div>
              <div className="text-muted-foreground">{t('offline.included.parts')}</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 text-center">
              <div className="text-2xl font-semibold text-accent">8</div>
              <div className="text-muted-foreground">{t('offline.included.principles')}</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 text-center">
              <div className="text-2xl font-semibold text-accent">14</div>
              <div className="text-muted-foreground">{t('offline.included.glossary')}</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-center">
            {t('offline.included.note')}
          </p>
        </CardContent>
      </Card>

      {/* Privacy Note */}
      <Card className="mt-6 bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
            <div>
              <h4 className="font-medium">{t('offline.included.privacyTitle')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('offline.included.privacyDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
